import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { useStore } from 'src/stores/store';
import { IRentPayment } from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import { GetMonthName } from 'src/sections/common/GetMonthName';
import Label from 'src/components/Label';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import useThrottle from 'src/hooks/useThrottle';
// ----------------------------------------------------------------------
interface Props {
  id: number;
}
export default observer(function RentPaymentNewEditForm({ id }: Props) {
  const { RentPaymentStore } = useStore();
  const { translate } = useLocales();
  const [selectedType, setSelectedType] = useState('');

  const {
    updateRentPayment,
    editMode,
    selectedRentPaymentDetail,
    clearSelectedRentPayment,
    setOpenCloseCreateDialog,
  } = RentPaymentStore;
  const [checkedAdvance, setCheckedAdvance] = useState(false);
  const [checkedRemit, setCheckedRemit] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const NewRentPaymentSchema = Yup.object().shape({
    paidAmount: Yup.string().required(`${translate('Validation.paidAmount')}`),
    paymentDate: Yup.string().required(`${translate('Validation.paymentDate')}`),
    description: Yup.string().required(`${translate('Validation.description')}`),
  });

  const defaultValues = useMemo<IRentPayment>(
    () => ({
      rentPaymentId: selectedRentPaymentDetail?.rentPaymentId,
      contractId: selectedRentPaymentDetail?.contractId || undefined,
      year: selectedRentPaymentDetail?.year || undefined,
      month: selectedRentPaymentDetail?.month || undefined,
      recievedByUserId: selectedRentPaymentDetail?.recievedByUserId || undefined,
      paidAmount:
        selectedRentPaymentDetail?.paidAmount === 0
          ? selectedRentPaymentDetail?.paidAmount
          : selectedRentPaymentDetail?.remainAmount || undefined,
      paymentDate: selectedRentPaymentDetail?.paymentDate || new Date(),
      description: selectedRentPaymentDetail?.description || '',
      attachment: selectedRentPaymentDetail?.attachment || undefined,
      serialNumber: selectedRentPaymentDetail?.serialNumber || undefined,
      remainAmount: selectedRentPaymentDetail?.remainAmount || undefined,
      paymentMethod: selectedRentPaymentDetail?.paymentMethod || 0,
      // RecieveFromAdvanceAmount: selectedRentPaymentDetail?.RecieveFromAdvanceAmount || undefined,
      // IsRecievedFromAdvance: selectedRentPaymentDetail?.IsRecievedFromAdvance || false,
      // IsRemit: selectedRentPaymentDetail?.IsRemit || false,
    }),
    [selectedRentPaymentDetail]
  );

  const methods = useForm<IRentPayment>({
    resolver: yupResolver(NewRentPaymentSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data: IRentPayment) => {
    const newData = {
      ...data,
      paymentDate: new Date(data.paymentDate!),
      paidAmount: Number(val.paidAmount) + Number(selectedRentPaymentDetail?.paidAmount),
    };

    try {
      await updateRentPayment(newData);
      reset();
      setOpenCloseCreateDialog();
      enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
      // navigate(PATH_DASHBOARD.RentPayment.list);
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      } catch {
        setError('afterSubmit', { type: 'manual', message: err.message || 'Unexpected error' });
      }
    }
  };


  useEffect(() => {
    setValue('paidAmount', selectedRentPaymentDetail?.remainAmount);
  }, [selectedRentPaymentDetail?.remainAmount]);

  const val = watch();

  const paidAmount = watch('paidAmount');

  const handleChangeAdvance = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedAdvance(event.target.checked);
  };

  const handleChangeRemit = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedRemit(event.target.checked);
  };
  const FormTypes = [
    { type: 0, value: 'CASH' },
    { type: 1, value: 'FREE' },
    { type: 2, value: 'TAKEN_FROM_ADVANCE' },

  ];

  // console.log(val.paymentMethod)

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* First column for the form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              <RHFSelect
                name="paymentMethod"
                label={translate('RentPayment.PaymentMehtod')}
              >
                <option value="" />
                {FormTypes.map((op, index) => (
                  <option key={index} value={op.type}>
                    {translate(`RentPayment.${op.value}`)}
                  </option>
                ))}
              </RHFSelect>
              {val.paymentMethod == 0 && (
                <RHFTextField
                  name="paidAmount"
                  label={translate('RentPayment.PaidAmount')}
                  type="number"
                  // onChange={(e) => {
                  //   const value = e.target.value === '' ? undefined : Number(e.target.value);
                  //   setValue('paidAmount', value);
                  // }}
                  autoFocus
                />
              )}
              <LocalizDatePicker
                name="paymentDate"
                label={translate('RentPayment.PaymentDate')}
                control={control}
                showAsterisk={true}
              />
              <RHFTextField
                name="description"
                label={translate('RentPayment.Description')}
                showAsterisk={true}
              />
              {val.paymentMethod == 0 && (
                <RHFTextField
                  name="serialNumber"
                  label={translate('RentPaymentPrint.SerialNumber')}
                  showAsterisk={true}
                />
              )}
              <Stack spacing={2} sx={{ mb: 2 }}>
                <>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    // accept=".jpg"
                    id="contractattachment"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setValue('attachment', file);
                    }}
                    style={{ display: 'none' }}
                  />

                  {/* TextField with Attachment Icon */}
                  <TextField
                    label="Attach File"
                    value={watch('attachment')?.name || ''} // Display file name
                    placeholder="No file chosen"
                    InputProps={{
                      readOnly: true, // Prevent manual input
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* Attachment Icon */}
                          <label htmlFor="contractattachment">
                            <IconButton component="span">
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    size="medium"
                    sx={{ m: 1, width: '100%' }}
                  />
                </>
              </Stack>

              {/* {!checkedAdvance && !checkedRemit ? (
                <div style={{ display:"flex" }}>
                <Controller
                  name="IsRecievedFromAdvance"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          onChange={(e) => {
                            field.onChange(e); // Notify React Hook Form of the value change
                            handleChangeAdvance(e); // Handle the switch state change
                          }}
                          checked={field.value}
                        />
                      }
                      label={translate('RecievedFromAdvance')}
                      labelPlacement="end"
                    />
                  )}
                />
             

             
                <Controller
                  name="IsRemit"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          onChange={(e) => {
                            field.onChange(e); // Notify React Hook Form of the value change
                            handleChangeRemit(e); // Handle the switch state change
                          }}
                          checked={field.value}
                        />
                      }
                      label={translate('IsRemit')}
                      labelPlacement="end"
                    />
                  )}
                />
                </div>
              ) : (
                ''
              )}
              {checkedAdvance ? (
                <RHFTextField
                  name="RecieveFromAdvanceAmount"
                  label={translate('RentPayment.RecieveFromAdvanceAmount')}
                  type="number"
                  // onChange={(e) => {
                  //   const value = e.target.value === '' ? undefined : Number(e.target.value);
                  //   setValue('paidAmount', value);
                  // }}
                  autoFocus
                />
              ) : (
                ''
              )} */}
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  setOpenCloseCreateDialog();
                  clearSelectedRentPayment();
                }}
              >
                {translate('CRUD.Close')}
              </Button>
            </Stack>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Label color="success">
                <Typography variant="body1">
                  {translate('RentPayment.Rentof')} {GetMonthName(selectedRentPaymentDetail?.month)}{' '}
                  /{selectedRentPaymentDetail?.year}
                </Typography>
              </Label>

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('Customer.CustomerName')}:</strong>
                    </TableCell>
                    <TableCell>{selectedRentPaymentDetail?.customerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('Shop.ShopCode')}:</strong>
                    </TableCell>
                    <TableCell>{selectedRentPaymentDetail?.shopCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('contractsInfo.ContractNumber')}:</strong>
                    </TableCell>
                    <TableCell>{selectedRentPaymentDetail?.contractNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('RentPayment.PaymentAmount')}:</strong>
                    </TableCell>
                    <TableCell>{selectedRentPaymentDetail?.paymentAmount}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('RentPayment.PaidAmount')}:</strong>
                    </TableCell>
                    <TableCell>
                      {selectedRentPaymentDetail?.paidAmount === 0
                        ? paidAmount
                        : Number(selectedRentPaymentDetail?.paidAmount!) + Number(paidAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('RentPayment.RemainAmount')}:</strong>
                    </TableCell>
                    <TableCell>
                      {selectedRentPaymentDetail?.paidAmount === 0
                        ? Number(selectedRentPaymentDetail?.paymentAmount!) - Number(paidAmount)
                        : Number(selectedRentPaymentDetail?.paymentAmount!) -
                        (Number(selectedRentPaymentDetail?.paidAmount!) + Number(paidAmount))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
